import React, { createContext, useContext, useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { InventoryService } from "../services";
import { useFiltersContext } from "domain/Filters";
import { useSortingContext } from "domain/Sorting";
import { useCategoriesContext } from "domain/Category";
import { useCompaniesContext } from "domain/Company";
import { useSuppliersContext } from "domain/Supplier/context";
import { PAGINATION_DEFAULTS, PAGINATION_QUERY_KEYS } from "shared/constants/constants";

const InventoryPaginationContext = createContext();

let lastList = null;

const sortObjectKeys = (value) =>
  value && typeof value === "object" && !Array.isArray(value)
    ? Object.keys(value)
        .sort()
        .reduce((sorted, key) => ({ ...sorted, [key]: value[key] }), {})
    : value;

const getContentKey = (value) => JSON.stringify(value, (_, nested) => sortObjectKeys(nested));

const getListKey = (filters, sorting, page, pageSize) => getContentKey({ filters, sorting, page, pageSize });

export function useInventoryPaginationContext() {
  return useContext(InventoryPaginationContext);
}

export function InventoryPaginationProvider({ children, rememberLastList = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { applyFilters: latestApplyFilters } = useFiltersContext();
  const { applySorting: latestApplySorting } = useSortingContext();

  // Re-applying identical filters/sorting creates new objects; only react when their content changes.
  const filtersContentKey = getContentKey(latestApplyFilters);
  const sortingContentKey = getContentKey(latestApplySorting);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applyFilters = useMemo(() => latestApplyFilters, [filtersContentKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applySorting = useMemo(() => latestApplySorting, [sortingContentKey]);

  const currentPage = useMemo(
    () => parseInt(searchParams.get(PAGINATION_QUERY_KEYS.PAGE) || PAGINATION_DEFAULTS.PAGE, 10),
    [searchParams]
  );
  const pageSize = useMemo(
    () => parseInt(searchParams.get(PAGINATION_QUERY_KEYS.PAGE_SIZE) || PAGINATION_DEFAULTS.PAGE_SIZE, 10),
    [searchParams]
  );

  const [rememberedList] = useState(() => {
    if (!rememberLastList || !lastList) return null;
    return lastList.key === getListKey(applyFilters, applySorting, currentPage, pageSize) ? lastList : null;
  });

  const [items, setItems] = useState(() => rememberedList?.items || []);
  const [totalItems, setTotalItems] = useState(() => rememberedList?.totalItems || 0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingRememberedList, setIsRefreshingRememberedList] = useState(!!rememberedList);
  const latestRequestId = useRef(0);
  const abortController = useRef(null);

  const [selectedItems, setSelectedItems] = useState([]);

  const { categories } = useCategoriesContext();
  const { companies, temporaryOwners } = useCompaniesContext();
  const allCompanyLikeEntities = [...companies, ...temporaryOwners];
  const { suppliers } = useSuppliersContext();

  const updateSelection = (selectionArray) => {
    setSelectedItems(selectionArray);
  };

  useEffect(() => {
    const needsUpdate =
      !searchParams.has(PAGINATION_QUERY_KEYS.PAGE) || !searchParams.has(PAGINATION_QUERY_KEYS.PAGE_SIZE);
    if (needsUpdate) {
      const newParams = new URLSearchParams(searchParams);
      if (!newParams.has(PAGINATION_QUERY_KEYS.PAGE)) {
        newParams.set(PAGINATION_QUERY_KEYS.PAGE, PAGINATION_DEFAULTS.PAGE);
      }
      if (!newParams.has(PAGINATION_QUERY_KEYS.PAGE_SIZE)) {
        newParams.set(PAGINATION_QUERY_KEYS.PAGE_SIZE, PAGINATION_DEFAULTS.PAGE_SIZE);
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const refetch = useCallback(() => {
    if (!categories.length || !companies.length || !suppliers.length) return;

    const params = { ...applyFilters, ...applySorting, page: currentPage, pageSize };
    const listKey = getListKey(applyFilters, applySorting, currentPage, pageSize);
    const requestId = ++latestRequestId.current;
    abortController.current?.abort();
    abortController.current = new AbortController();
    setIsLoading(true);
    setSelectedItems([]);
    setIsRefreshingRememberedList((isRefreshing) => isRefreshing && listKey === rememberedList?.key);

    InventoryService.findPage(params, { signal: abortController.current.signal })
      .then((response) => {
        if (requestId !== latestRequestId.current) return;

        const { data, total } = response;
        const processedData = data.map((item) => {
          const { companyId, categoryId, ownerId, supplierId, ...other } = item;
          const company = companies.find((c) => c.id === companyId);
          const owner = allCompanyLikeEntities.find((o) => o.id === ownerId);
          const category = categories.find((c) => c.id === categoryId);
          const supplier = suppliers.find((s) => s.id === item.invoice?.supplierId);

          return {
            ...other,
            company,
            category,
            owner,
            supplier,
            parts: (item.parts || []).map((part) => {
              return {
                ...part,
                category: categories.find((c) => c.id === part.categoryId),
                company: companies.find((c) => c.id === part.companyId),
                owner: allCompanyLikeEntities.find((o) => o.id === part.ownerId),
                supplier: suppliers?.find((s) => s.id === part.invoice?.supplierId),
              };
            }),
          };
        });
        setItems(processedData);
        setTotalItems(total);
        if (rememberLastList) lastList = { key: listKey, items: processedData, totalItems: total };
      })
      .catch((error) => {
        if (requestId !== latestRequestId.current) return;
        throw error;
      })
      .finally(() => {
        if (requestId !== latestRequestId.current) return;
        setIsLoading(false);
        setIsRefreshingRememberedList(false);
      });
  }, [
    applyFilters,
    applySorting,
    currentPage,
    pageSize,
    categories,
    companies,
    suppliers,
    rememberLastList,
    rememberedList,
  ]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(
    () => () => {
      latestRequestId.current += 1;
      abortController.current?.abort();
    },
    []
  );

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(PAGINATION_QUERY_KEYS.PAGE, newPage.toString());
    setSearchParams(newParams);
  };

  const addItem = async (item, count, serialNumbers = []) => {
    const { isActive } = item;
    const itemCopy = { ...item, activationDate: isActive ? new Date() : null };

    let serialNumbersToUse = item.serialNumbers || serialNumbers;
    if (serialNumbersToUse.length === 0) {
      serialNumbersToUse = Array(count).fill("");
    }

    let itemsToCreate = [];

    if (item.category?.name === "Radna stanica") {
      const { serialNumbers, ...rest } = itemCopy;
      itemsToCreate = [rest];
    } else {
      itemsToCreate = serialNumbersToUse.map((sn) => {
        const { serialNumbers, ...rest } = itemCopy;
        return {
          ...rest,
          serialNumber: sn,
        };
      });
    }

    const results = await Promise.all(itemsToCreate.map((newItem) => InventoryService.add(newItem, 1)));

    const normalized = Array.isArray(results) ? results.flat() : [results];

    refetch();

    return normalized;
  };

  const deleteInventory = async (inventoryId, deleteReason) => {
    return InventoryService.deleteInventory(inventoryId, deleteReason).then(() => {
      refetch();
    });
  };

  const deleteInventories = async (inventoryIds, deleteReason) => {
    return await InventoryService.deleteInventories(inventoryIds, deleteReason).then(() => {
      refetch();
    });
  };

  const updateItem = async (item) => {
    return InventoryService.update(item).then((inventory) => {
      refetch();
      return inventory.updatedInventory;
    });
  };

  const value = {
    items,
    totalItems,
    isLoading,
    isRefreshingRememberedList,
    currentPage,
    pageSize,
    handlePageChange,
    refetch,
    addItem,
    deleteInventory,
    updateItem,
    selectedItems,
    updateSelection,
    deleteInventories,
  };

  return <InventoryPaginationContext.Provider value={value}>{children}</InventoryPaginationContext.Provider>;
}
